<template>
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 z-[9999] overflow-hidden">
      <button
        v-if="!requiredSetup"
        type="button"
        class="absolute inset-0 bg-black/50"
        aria-label="Fermer la configuration PrestaShop"
        @click="close"
      ></button>
      <div
        class="relative flex h-full items-center justify-center p-3 pointer-events-none"
      >
      <div
        ref="panel"
        class="pointer-events-auto relative bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[calc(100dvh-1.5rem)] min-h-0 flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="prestashop-configuration-title"
      >
        <div
          class="shrink-0 p-5 border-b border-gray-200 flex items-start justify-between gap-4"
        >
          <div>
            <h2
              id="prestashop-configuration-title"
              class="text-lg font-semibold text-black"
            >
              Configuration PrestaShop
            </h2>
            <p class="text-sm text-gray-600 mt-1">
              {{
                requiredSetup
                  ? "Choisissez le genre, le pays de provenance et le type de vente avant le premier import."
                  : "Associez une fois les libellés PrestaShop à vos valeurs eFashion."
              }}
            </p>
          </div>
          <button
            v-if="!requiredSetup"
            type="button"
            class="relative z-10 shrink-0 rounded-md p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Fermer"
            @click="close"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div
          class="shrink-0 min-h-[52px] border-b border-gray-200 px-5 flex gap-1 overflow-x-auto"
          role="tablist"
          aria-label="Configuration PrestaShop"
        >
          <button
            v-for="item in tabs"
            :key="item.id"
            type="button"
            role="tab"
            class="shrink-0 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 flex items-center gap-1.5"
            :class="
              tab === item.id
                ? 'border-green-600 text-green-700'
                : 'border-transparent text-gray-600'
            "
            :disabled="busy || (requiredSetup && item.id !== 'general')"
            :aria-selected="tab === item.id"
            @click="selectTab(item.id)"
          >
            {{ item.label }}
            <span
              v-if="item.id !== 'general' && pendingCount(item.id)"
              class="inline-flex items-center gap-1 text-red-600"
              :aria-label="pendingCount(item.id) + ' correspondances à traiter'"
            >
              <i class="fas fa-exclamation-circle text-xs"></i>
              <span class="text-xs">{{ pendingCount(item.id) }}</span>
            </span>
          </button>
        </div>

        <div
          ref="scrollBody"
          class="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5 space-y-4"
        >
          <p
            v-if="message"
            class="text-sm rounded-lg p-3"
            :class="
              messageIsError
                ? 'bg-red-50 text-red-700'
                : 'bg-green-50 text-green-700'
            "
          >
            {{ message }}
          </p>
          <div v-if="tab === 'general'" class="max-w-xl space-y-4">
            <label
              class="block text-sm font-medium text-gray-800"
              for="prestashop-default-gender"
              >Genre par défaut</label
            >
            <select
              id="prestashop-default-gender"
              v-model="defaultGender"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Choisir un genre</option>
              <option value="femme">Femme</option>
              <option value="homme">Homme</option>
              <option value="enfant">Enfant</option>
              <option value="aucun">
                Plusieurs genres / aucun genre par défaut
              </option>
            </select>
            <p class="text-xs text-gray-600">
              Cette préférence aide à proposer une catégorie. Les catégories
              ambiguës restent à valider.
            </p>
            <label
              class="block text-sm font-medium text-gray-800"
              for="prestashop-default-provenance"
              >Pays de provenance par défaut</label
            >
            <select
              id="prestashop-default-provenance"
              v-model.number="defaultProvenanceId"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Choisir un pays</option>
              <option
                v-for="provenance in provenances"
                :key="provenance.id"
                :value="provenance.id"
              >
                {{ provenance.label }}
              </option>
            </select>
            <p class="text-xs text-gray-600">
              Ce pays est utilisé seulement si PrestaShop ne renseigne pas une
              provenance reconnue pour le produit.
            </p>
            <label
              class="block text-sm font-medium text-gray-800"
              for="prestashop-default-sold-by"
              >Type de vente par défaut</label
            >
            <select
              id="prestashop-default-sold-by"
              v-model="defaultSoldBy"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Choisir un type de vente</option>
              <option value="couleurs">À la couleur</option>
              <option value="melangees">Couleurs mélangées</option>
              <option value="tailles">Tailles aux choix</option>
            </select>
            <p class="text-xs text-gray-600">
              Ce choix est mis sur les fiches seulement si PrestaShop n’écrit
              pas explicitement couleurs, couleurs mélangées ou tailles.
            </p>
            <div class="space-y-4 rounded-lg border border-gray-200 p-4">
              <div>
                <h3 class="text-sm font-semibold text-gray-900">
                  Si non trouvé
                </h3>
                <p class="text-xs text-gray-600">
                  Taille et quantité de paquet de ce vendeur. Elles sont
                  utilisées seulement quand PrestaShop n’en fournit pas déjà une
                  reconnue.
                </p>
              </div>
              <label
                class="block text-sm font-medium text-gray-800"
                for="prestashop-default-declinaison"
                >Taille du paquet</label
              >
              <select
                id="prestashop-default-declinaison"
                v-model="defaultDeclinaisonId"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Aucune</option>
                <option
                  v-for="declinaison in declinaisons"
                  :key="declinaison.id"
                  :value="Number(declinaison.id)"
                >
                  {{ declinaisonOptionLabel(declinaison) }}
                </option>
              </select>
              <p v-if="!declinaisons.length" class="text-xs text-amber-700">
                Ce vendeur n’a pas encore de taille de paquet.
              </p>
              <button
                type="button"
                class="rounded-lg border border-green-600 px-3 py-2 text-sm font-medium text-green-700 disabled:opacity-50"
                :disabled="busy"
                @click="createUnitDefaults"
              >
                Créer et sélectionner TU · pack 1
              </button>
              <template v-if="defaultDeclinaisonId">
                <label
                  class="block text-sm font-medium text-gray-800"
                  for="prestashop-default-pack"
                  >Quantité du paquet</label
                >
                <select
                  id="prestashop-default-pack"
                  v-model="defaultPackId"
                  class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Aucune</option>
                  <option
                    v-for="pack in packsForDefaultDeclinaison"
                    :key="pack.id"
                    :value="Number(pack.id)"
                  >
                    {{ packOptionLabel(pack) }}
                  </option>
                </select>
                <p v-if="!packs.length" class="text-xs text-amber-700">
                  Ce vendeur n’a pas encore de quantité de paquet.
                </p>
                <p
                  v-else-if="!packsForDefaultDeclinaison.length"
                  class="text-xs text-amber-700"
                >
                  Aucune quantité ne correspond à cette taille de paquet.
                </p>
              </template>
            </div>
            <label
              class="block text-sm font-medium text-gray-800"
              for="prestashop-price-adjustment"
              >Coefficient de prix</label
            >
            <select
              id="prestashop-price-adjustment"
              v-model.number="priceAdjustmentPercent"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option :value="0">0 % — prix PrestaShop tel quel</option>
              <option :value="5">+5 %</option>
              <option :value="10">+10 %</option>
            </select>
            <label
              class="block text-sm font-medium text-gray-800"
              for="prestashop-price-is-unit"
              >Le prix PrestaShop est déjà à l’unité</label
            >
            <select
              id="prestashop-price-is-unit"
              v-model="priceIsUnit"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option :value="false">
                Non — prix du paquet, divisé par la quantité totale
              </option>
              <option :value="true">Oui — prix déjà unitaire</option>
            </select>
            <button
              type="button"
              class="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              :disabled="
                busy ||
                !defaultGender ||
                !defaultProvenanceId ||
                !defaultSoldBy ||
                !defaultDeclinaisonId ||
                !defaultPackId
              "
              @click="saveGeneral"
            >
              {{ busy ? "Enregistrement…" : "Enregistrer la configuration" }}
            </button>
          </div>

          <div v-else-if="tab === 'weight'" class="max-w-xl space-y-4">
            <div>
              <label
                class="block text-sm font-medium text-gray-800"
                for="prestashop-weight-is-unit"
                >Le poids PrestaShop est déjà à l’unité</label
              >
              <select
                id="prestashop-weight-is-unit"
                v-model="weightIsUnit"
                class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option :value="false">
                  Non — poids du paquet, divisé par la quantité totale
                </option>
                <option :value="true">Oui — poids déjà unitaire</option>
              </select>
              <p class="text-xs text-gray-600 mt-1">
                Ce choix s’applique uniquement au poids PrestaShop. Un poids de
                catégorie est toujours considéré comme un poids unitaire. Si le
                poids PrestaShop correspond au paquet, il est divisé par la
                quantité du paquet sur la fiche. Si le poids unitaire reste
                au-dessus de 5 kg, la fiche reste bloquée.
              </p>
              <button
                type="button"
                class="mt-3 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                :disabled="
                  busy ||
                  !defaultGender ||
                  !defaultProvenanceId ||
                  !defaultSoldBy ||
                  !defaultDeclinaisonId ||
                  !defaultPackId
                "
                @click="saveGeneral"
              >
                {{ busy ? "Enregistrement…" : "Enregistrer" }}
              </button>
            </div>
            <div>
              <h3 class="text-sm font-semibold text-gray-900">
                Poids par catégorie
              </h3>
              <p class="text-xs text-gray-600 mt-1">
                Chaque produit de la catégorie choisie prend ce poids. Le poids
                PrestaShop est ignoré.
              </p>
            </div>
            <CategoryTreeAutocomplete
              :key="weightFormKey"
              :tree="categoriesTree"
              cat-id=""
              sous-id=""
              sous-sous-id=""
              label="Catégorie"
              placeholder="Rechercher une catégorie, par exemple bottes…"
              @select="onWeightCategorySelect"
            />
            <p v-if="weightCategoryLabel" class="text-xs text-gray-600">
              Catégorie choisie : {{ weightCategoryLabel }}
            </p>
            <label
              class="block text-sm font-medium text-gray-800"
              for="prestashop-category-weight"
              >Poids (kg)</label
            >
            <input
              id="prestashop-category-weight"
              v-model="categoryWeightInput"
              type="number"
              min="0.1"
              step="0.1"
              placeholder="25"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              class="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              :disabled="busy || !weightCategoryId || !categoryWeightInput"
              @click="saveCategoryWeightRule"
            >
              {{ busy ? "Enregistrement…" : "Enregistrer ce poids" }}
            </button>
            <div class="space-y-2">
              <h3 class="text-sm font-semibold text-gray-900">
                Règles enregistrées
              </h3>
              <div v-if="loading" class="text-sm text-gray-500">
                Chargement…
              </div>
              <p
                v-else-if="!categoryWeights.length"
                class="text-sm text-gray-500"
              >
                Aucun poids de catégorie.
              </p>
              <div
                v-for="row in categoryWeights"
                :key="row.categoryId"
                class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 p-3"
              >
                <div class="text-sm">
                  <span class="font-medium">{{ row.categoryLabel }}</span>
                  <span class="mx-2 text-gray-400">→</span>
                  <span>{{ formatCategoryWeight(row.weightKg) }} kg</span>
                </div>
                <button
                  type="button"
                  class="text-sm text-red-700 hover:underline"
                  :disabled="busy"
                  @click="removeCategoryWeightRule(row.categoryId)"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>

          <div v-else class="space-y-5">
            <div
              class="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500"
            >
              <span
                >Les correspondances sont calculées depuis le catalogue
                PrestaShop.</span
              >
              <button
                type="button"
                class="text-blue-700 hover:underline disabled:opacity-50"
                :disabled="pendingLoading || busy"
                @click="loadPending(true)"
              >
                <i class="fas fa-sync-alt mr-1"></i> Recalculer les
                correspondances
              </button>
            </div>
            <div
              v-if="pendingLoading"
              class="text-sm text-gray-500"
              role="status"
            >
              <i class="fas fa-spinner fa-spin mr-1"></i> Analyse du catalogue
              PrestaShop en cours…
            </div>
            <p v-if="!catalogReady" class="text-sm text-blue-700">
              Les correspondances sont lues directement sur la boutique
              PrestaShop.
            </p>
            <p
              v-if="pendingSummary?.incomplete"
              class="rounded-lg bg-amber-50 p-3 text-xs text-amber-800"
            >
              Le catalogue est très volumineux : les alertes couvrent les
              {{ pendingSummary.scannedProducts }} premiers produits.
            </p>
            <div class="space-y-2">
              <h3 class="text-sm font-semibold text-gray-900">
                À lier {{ pending.length ? "(" + pending.length + ")" : "" }}
              </h3>
              <p
                v-if="!pending.length && !pendingLoading"
                class="text-sm text-green-700"
              >
                {{
                  pendingSummary
                    ? "Toutes les valeurs détectées sont liées."
                    : "Aucune correspondance détectée pour le moment."
                }}
              </p>
              <div
                v-for="row in pendingRows"
                :key="`${row.sourceId || 'label'}-${row.label}`"
                class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3"
              >
                <span
                  class="flex items-center gap-2 text-sm font-medium text-gray-900"
                >
                  <span
                    v-if="tab === 'color' && colorHex(row.label)"
                    class="inline-block h-6 w-6 shrink-0 rounded-full border border-gray-400 shadow-sm"
                    :style="{
                      backgroundColor: colorHex(row.label) || undefined,
                    }"
                    :title="colorHex(row.label) || undefined"
                    :aria-label="'Couleur PrestaShop ' + colorHex(row.label)"
                  ></span>
                  <span>{{ row.label }}</span>
                  <span
                    v-if="row.sourceId"
                    class="text-xs font-normal text-gray-500"
                    >#{{ row.sourceId }}</span
                  >
                  <span
                    v-if="tab === 'color' && colorHex(row.label)"
                    class="text-xs font-normal text-gray-600"
                    >{{ colorHex(row.label) }}</span
                  >
                </span>
                <button
                  type="button"
                  class="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
                  :disabled="busy || loading"
                  @click="openPicker(row.label)"
                >
                  {{ chooseLabel }}
                </button>
              </div>
            </div>

            <div class="space-y-2">
              <h3 class="text-sm font-semibold text-gray-900">
                Correspondances enregistrées
              </h3>
              <div v-if="loading" class="text-sm text-gray-500">
                Chargement…
              </div>
              <p v-else-if="!mappings.length" class="text-sm text-gray-500">
                Aucune correspondance enregistrée.
              </p>
              <div
                v-for="row in mappings"
                :key="row.sourceLabelNormalized"
                class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 p-3"
              >
                <div class="text-sm min-w-0">
                  <span class="font-medium">{{ row.sourceLabel }}</span>
                  <span class="mx-2 text-gray-400">→</span>
                  <span>{{ row.targetLabel || "#" + row.targetId }}</span>
                </div>
                <div class="flex items-center gap-3">
                  <button
                    type="button"
                    class="text-sm text-blue-700 hover:underline"
                    :disabled="busy || loading"
                    @click="openPicker(row.sourceLabel)"
                  >
                    Modifier
                  </button>
                  <button
                    type="button"
                    class="text-sm text-red-700 hover:underline"
                    :disabled="busy"
                    @click="remove(row)"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>

            <div class="border-t border-gray-100 pt-3">
              <button
                type="button"
                class="text-xs text-gray-600 hover:underline"
                @click="manualOpen = !manualOpen"
              >
                {{
                  manualOpen
                    ? "Masquer l'ajout manuel"
                    : "Ajouter un libellé PrestaShop absent de cette liste"
                }}
              </button>
              <div v-if="manualOpen" class="mt-3 flex flex-wrap gap-2">
                <input
                  v-model.trim="manualSource"
                  type="text"
                  maxlength="255"
                  aria-label="Libellé PrestaShop"
                  placeholder="Libellé PrestaShop"
                  class="flex-1 min-w-[180px] rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  class="rounded-lg bg-gray-800 px-3 py-2 text-sm text-white disabled:opacity-50"
                  :disabled="!manualSource || busy || loading"
                  @click="openPicker(manualSource)"
                >
                  Choisir une valeur
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  </Teleport>

  <CouleursPickerModal
    v-model:isVisible="colorPickerVisible"
    :couleurs-list="officialColors"
    :official-only="true"
    :selected-ids="[]"
    @confirm="onColorConfirm"
  />

  <Teleport to="body">
    <div
      v-if="pickerVisible"
      class="fixed inset-0 z-[10050] flex items-center justify-center p-3"
    >
      <button
        type="button"
        class="absolute inset-0 bg-black/50"
        aria-label="Fermer le choix"
        @click="pickerVisible = false"
      ></button>
      <div
        class="relative flex max-h-[85vh] w-full max-w-xl flex-col rounded-xl bg-white p-5 shadow-xl"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <h3 class="font-semibold text-gray-900">{{ chooseLabel }}</h3>
            <p class="text-sm text-gray-600 mt-1">
              Valeur PrestaShop : <strong>{{ pickerSource }}</strong>
            </p>
          </div>
          <button
            type="button"
            aria-label="Fermer"
            class="text-gray-500"
            @click="pickerVisible = false"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <p
          v-if="messageIsError && message"
          class="mt-3 rounded-lg bg-red-50 p-2 text-sm text-red-700"
        >
          {{ message }}
        </p>

        <div v-if="tab === 'category'" class="mt-5">
          <CategoryTreeAutocomplete
            :tree="categoriesTree"
            cat-id=""
            sous-id=""
            sous-sous-id=""
            label="Catégorie eFashion"
            placeholder="Rechercher une catégorie feuille…"
            @select="onCategorySelect"
          />
        </div>
        <div
          v-else-if="tab === 'composition'"
          class="mt-5 flex min-h-0 flex-col gap-3"
        >
          <input
            v-model.trim="pickerSearch"
            type="search"
            aria-label="Rechercher une matière"
            placeholder="Rechercher une matière"
            class="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <div
            class="min-h-0 overflow-y-auto rounded-lg border border-gray-200"
          >
            <button
              v-for="target in filteredTargets"
              :key="target.id"
              type="button"
              class="block w-full border-b border-gray-100 px-3 py-2 text-left text-sm hover:bg-green-50"
              :disabled="busy"
              @click="savePickedTarget(target.id)"
            >
              {{ target.label }}
            </button>
            <p v-if="!filteredTargets.length" class="p-3 text-sm text-gray-500">
              Aucune matière trouvée.
            </p>
          </div>
        </div>
        <div
          v-else-if="tab === 'brand'"
          class="mt-5 min-h-0 overflow-y-auto space-y-3"
        >
          <div class="rounded-lg border border-gray-200">
            <button
              type="button"
              class="w-full px-3 py-2 text-left text-sm font-medium flex justify-between"
              @click="brandListOpen = !brandListOpen"
            >
              Choisir une marque existante ({{ targets.length }})
              <i class="fas fa-chevron-down text-xs"></i>
            </button>
            <div
              v-if="brandListOpen"
              class="border-t border-gray-200 p-3 space-y-2"
            >
              <input
                v-model.trim="pickerSearch"
                type="search"
                aria-label="Rechercher une marque"
                placeholder="Rechercher une marque"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <div class="max-h-52 overflow-y-auto">
                <button
                  v-for="target in filteredTargets"
                  :key="target.id"
                  type="button"
                  class="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-green-50"
                  :disabled="busy"
                  @click="savePickedTarget(target.id)"
                >
                  {{ target.label }}
                  <span class="text-xs text-gray-500"
                    >#{{ target.id
                    }}{{ target.isDefault ? " · par défaut" : "" }}</span
                  >
                </button>
                <p v-if="!filteredTargets.length" class="text-sm text-gray-500">
                  Aucune marque trouvée.
                </p>
              </div>
            </div>
          </div>
          <div class="rounded-lg border border-gray-200">
            <button
              type="button"
              class="w-full px-3 py-2 text-left text-sm font-medium flex justify-between"
              @click="brandCreateOpen = !brandCreateOpen"
            >
              Créer une nouvelle marque
              <i class="fas fa-chevron-down text-xs"></i>
            </button>
            <div
              v-if="brandCreateOpen"
              class="border-t border-gray-200 p-3 space-y-2"
            >
              <label
                for="prestashop-new-brand"
                class="block text-xs text-gray-600"
                >Nom de la nouvelle marque</label
              >
              <input
                id="prestashop-new-brand"
                v-model.trim="brandName"
                maxlength="255"
                type="text"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                class="rounded-lg bg-green-600 px-3 py-2 text-sm text-white disabled:opacity-50"
                :disabled="busy || !brandName"
                @click="createBrand"
              >
                Créer et lier
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import CouleursPickerModal from "@/components/mel/CouleursPickerModal.vue";
import CategoryTreeAutocomplete from "@/components/forms/CategoryTreeAutocomplete.vue";
import { getCouleursOfficielles } from "@/api/couleurs";
import { getCategoriesTree } from "@/api/categories";
import { getReferenceData } from "@/api/shooting";
import {
  createPrestashopBrandMapping,
  deletePrestashopMapping,
  ensurePrestashopUnitDefaults,
  getPrestashopMappingTargets,
  getPrestashopMappings,
  getPrestashopPendingMappings,
  getPrestashopSettings,
  getPrestashopProvenances,
  getPrestashopCategoryWeights,
  savePrestashopCategoryWeight,
  deletePrestashopCategoryWeight,
  savePrestashopMapping,
  savePrestashopSettings,
  type PrestashopCategoryWeight,
  type PrestashopMapping,
  type PrestashopMappingTarget,
  type PrestashopMappingType,
  type PrestashopPendingMappings,
  type PrestashopSettings,
} from "@/api/prestashop";
import {
  formatDeclinaisonLabel,
  getDeclinaisonLength,
} from "@/utils/declinaisonLabel";
import type { PrestashopReferenceOption } from "@/types/prestashop";
import type { CouleurAPI } from "@/types/mel-components";
import type { CategorieNode, CategoryPathSelection } from "@/types/categorie";

type Tab = "general" | "weight" | PrestashopMappingType;
const props = defineProps<{
  visible: boolean;
  shopUrl?: string;
  requiredSetup: boolean;
  catalogReady: boolean;
  catalogRevision: number;
  pendingColors: string[];
  pendingCategories: string[];
  pendingCompositions: string[];
  pendingBrands: string[];
}>();
const emit = defineEmits<{
  (event: "update:visible", value: boolean): void;
  (event: "changed"): void;
  (event: "settings-saved", value: PrestashopSettings): void;
}>();

const tabs: Array<{ id: Tab; label: string }> = [
  { id: "general", label: "Général" },
  { id: "color", label: "Couleurs" },
  { id: "category", label: "Catégories" },
  { id: "composition", label: "Compositions" },
  { id: "weight", label: "Poids" },
  { id: "brand", label: "Marques" },
];
const tab = ref<Tab>("general");
const defaultGender = ref<
  "" | NonNullable<PrestashopSettings["defaultGender"]>
>("");
const savedGender = ref<PrestashopSettings["defaultGender"]>(null);
const priceAdjustmentPercent = ref(0);
const priceIsUnit = ref(true);
const weightIsUnit = ref(true);
const defaultProvenanceId = ref<number | "">("");
const defaultSoldBy = ref<"" | NonNullable<PrestashopSettings["defaultSoldBy"]>>(
  ""
);
const savedProvenanceId = ref<number | null>(null);
const defaultDeclinaisonId = ref<number | "">("");
const defaultPackId = ref<number | "">("");
const declinaisons = ref<PrestashopReferenceOption[]>([]);
const packs = ref<PrestashopReferenceOption[]>([]);
const provenances = ref<Array<{ id: number; label: string }>>([]);
const mappings = ref<PrestashopMapping[]>([]);
const targets = ref<PrestashopMappingTarget[]>([]);
const pendingSummary = ref<PrestashopPendingMappings | null>(null);
const pendingLoading = ref(false);
const loading = ref(false);
const busy = ref(false);
const message = ref("");
const messageIsError = ref(false);
const pickerVisible = ref(false);
const colorPickerVisible = ref(false);
const pickerSource = ref("");
const pickerSearch = ref("");
const brandName = ref("");
const brandListOpen = ref(true);
const brandCreateOpen = ref(false);
const manualOpen = ref(false);
const manualSource = ref("");
const officialColors = ref<CouleurAPI[]>([]);
const categoriesTree = ref<CategorieNode[]>([]);
const categoryWeights = ref<PrestashopCategoryWeight[]>([]);
const weightCategoryId = ref<number | "">("");
const weightCategoryLabel = ref("");
const categoryWeightInput = ref("");
const weightFormKey = ref(0);
let loadSequence = 0;
let pendingSequence = 0;
const resolvedDuringScan = new Map<PrestashopMappingType, Set<string>>();

const pendingByType = computed(
  () =>
    pendingSummary.value || {
      color: props.pendingColors,
      category: props.pendingCategories,
      composition: props.pendingCompositions,
      brand: props.pendingBrands,
      colorHexByName: {},
      categorySources: [],
      scannedProducts: 0,
      incomplete: false,
    }
);
const pending = computed(() =>
  tab.value === "general" || tab.value === "weight"
    ? []
    : pendingByType.value[tab.value]
);
const pendingRows = computed(() => {
  if (
    tab.value === "category" &&
    pendingSummary.value?.categorySources?.length
  ) {
    return pendingSummary.value.categorySources.map((row) => ({
      label: row.sourceLabel,
      sourceId: row.sourceId,
    }));
  }
  return pending.value.map((label) => ({
    label,
    sourceId: null as number | null,
  }));
});
const chooseLabel = computed(() =>
  tab.value === "color"
    ? "Choisir une couleur"
    : tab.value === "category"
    ? "Choisir une catégorie"
    : tab.value === "composition"
    ? "Choisir une matière"
    : "Choisir une marque"
);
const filteredTargets = computed(() => {
  const search = normalize(pickerSearch.value);
  const rows = search
    ? targets.value.filter((target) => normalize(target.label).includes(search))
    : targets.value;
  if (tab.value !== "brand" || search) return rows;
  const source = normalize(pickerSource.value);
  const sourceWords = new Set(
    source.split(" ").filter((word) => word.length > 2 && word !== "b2b")
  );
  return [...rows].sort((left, right) => {
    const score = (target: PrestashopMappingTarget) =>
      normalize(target.label)
        .split(" ")
        .filter((word) => sourceWords.has(word)).length +
      (normalize(target.label) === source ? 10 : 0);
    return (
      score(right) - score(left) ||
      Number(right.isDefault) - Number(left.isDefault) ||
      left.label.localeCompare(right.label, "fr")
    );
  });
});

function optionSizeCount(value: string | number | undefined | null) {
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "").length;
}

function declinaisonSizeCount(
  declinaison: PrestashopReferenceOption | undefined
) {
  if (!declinaison) return 0;
  return (
    getDeclinaisonLength(declinaison.tailles) ||
    optionSizeCount(declinaison.tailles)
  );
}

function declinaisonOptionLabel(declinaison: PrestashopReferenceOption) {
  return (
    formatDeclinaisonLabel(declinaison.label, declinaison.tailles) ||
    String(declinaison.id)
  );
}

function packOptionLabel(pack: PrestashopReferenceOption) {
  const label = String(pack.label || pack.id);
  const value = String(pack.value ?? "").trim();
  if (!value || label.includes(`(${value})`)) return label;
  return `${label} (${value})`;
}

function packSizeCount(pack: PrestashopReferenceOption) {
  return optionSizeCount(pack.value);
}

function packOptionDisabled(pack: PrestashopReferenceOption) {
  const declinaison = declinaisons.value.find(
    (item) => Number(item.id) === Number(defaultDeclinaisonId.value)
  );
  const expected = declinaisonSizeCount(declinaison);
  if (!expected) return true;
  return packSizeCount(pack) !== expected;
}

const packsForDefaultDeclinaison = computed(() =>
  packs.value.filter((pack) => !packOptionDisabled(pack))
);

async function createUnitDefaults() {
  if (busy.value) return;
  busy.value = true;
  try {
    const defaults = await ensurePrestashopUnitDefaults();
    const references = await getReferenceData();
    declinaisons.value = references.declinaisons || [];
    packs.value = references.packs || [];
    defaultDeclinaisonId.value = defaults.defaultDeclinaisonId;
    defaultPackId.value = defaults.defaultPackId;
    showMessage("Déclinaison TU et pack unitaire prêts.");
  } catch (caught) {
    showMessage(
      caught instanceof Error ? caught.message : "Création impossible.",
      true
    );
  } finally {
    busy.value = false;
  }
}

function normalize(value: string) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}
function pendingCount(type: Tab) {
  if (type === "general" || type === "weight") return 0;
  return pendingByType.value[type].length;
}
function colorHex(label: string): string | null {
  const hex = pendingByType.value.colorHexByName[normalize(label)];
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex || "") ? hex : null;
}
function clearPending(type: PrestashopMappingType, source: string) {
  if (pendingLoading.value) {
    const resolved = resolvedDuringScan.get(type) || new Set<string>();
    resolved.add(normalize(source));
    resolvedDuringScan.set(type, resolved);
  }
  if (pendingSummary.value) {
    pendingSummary.value = {
      ...pendingSummary.value,
      [type]: pendingSummary.value[type].filter(
        (label) => normalize(label) !== normalize(source)
      ),
      ...(type === "category"
        ? {
            categorySources: pendingSummary.value.categorySources.filter(
              (row) => normalize(row.sourceLabel) !== normalize(source)
            ),
          }
        : {}),
    };
  }
}
function showMessage(value: string, isError = false) {
  message.value = value;
  messageIsError.value = isError;
}
function close() {
  if (props.requiredSetup) return;
  emit("update:visible", false);
}
const panel = ref<HTMLElement | null>(null);
const scrollBody = ref<HTMLElement | null>(null);

function scrollPastControls(event: WheelEvent) {
  const scroller = scrollBody.value;
  const target = event.target;
  if (!scroller || !(target instanceof Element)) return;
  const overSelect = Boolean(target.closest("select"));
  const overTabs = Boolean(target.closest("[role='tablist']"));
  if (!overSelect && !overTabs) return;
  if (overTabs && Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
  const maxScroll = scroller.scrollHeight - scroller.clientHeight;
  if (maxScroll <= 0) return;
  const delta =
    event.deltaMode === 1
      ? event.deltaY * 16
      : event.deltaMode === 2
        ? event.deltaY * scroller.clientHeight
        : event.deltaY;
  const next = Math.min(maxScroll, Math.max(0, scroller.scrollTop + delta));
  if (next === scroller.scrollTop) return;
  scroller.scrollTop = next;
  event.preventDefault();
}

function onEscape(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  if (colorPickerVisible.value) {
    colorPickerVisible.value = false;
    return;
  }
  if (pickerVisible.value) {
    pickerVisible.value = false;
    return;
  }
  close();
}

watch(panel, (element, previous) => {
  previous?.removeEventListener("wheel", scrollPastControls);
  element?.addEventListener("wheel", scrollPastControls, { passive: false });
});
watch(
  () => props.visible,
  (visible) => {
    if (visible) window.addEventListener("keydown", onEscape);
    else window.removeEventListener("keydown", onEscape);
  },
);
onUnmounted(() => {
  panel.value?.removeEventListener("wheel", scrollPastControls);
  window.removeEventListener("keydown", onEscape);
});
async function loadPending(force = false) {
  if (!props.catalogReady) return;
  if ((pendingLoading.value || pendingSummary.value) && !force) return;
  const sequence = ++pendingSequence;
  resolvedDuringScan.clear();
  pendingLoading.value = true;
  try {
    const summary = await getPrestashopPendingMappings();
    if (sequence === pendingSequence) {
      for (const type of [
        "color",
        "category",
        "composition",
        "brand",
      ] as const) {
        const resolved = resolvedDuringScan.get(type);
        if (resolved)
          summary[type] = summary[type].filter(
            (label) => !resolved.has(normalize(label))
          );
        if (type === "category" && resolved) {
          summary.categorySources = summary.categorySources.filter(
            (row) => !resolved.has(normalize(row.sourceLabel))
          );
        }
      }
      pendingSummary.value = summary;
    }
  } catch (caught) {
    if (sequence === pendingSequence)
      showMessage(
        caught instanceof Error
          ? caught.message
          : "Catalogue PrestaShop indisponible.",
        true
      );
  } finally {
    if (sequence === pendingSequence) {
      pendingLoading.value = false;
      resolvedDuringScan.clear();
    }
  }
}
async function loadTab(type: PrestashopMappingType) {
  const sequence = ++loadSequence;
  loading.value = true;
  try {
    const [nextMappings, nextTargets] = await Promise.all([
      getPrestashopMappings(type),
      getPrestashopMappingTargets(type),
    ]);
    if (sequence === loadSequence) {
      mappings.value = nextMappings;
      targets.value = nextTargets;
    }
  } catch (caught) {
    if (sequence === loadSequence)
      showMessage(
        caught instanceof Error ? caught.message : "Chargement impossible.",
        true
      );
  } finally {
    if (sequence === loadSequence) loading.value = false;
  }
}
function selectTab(value: Tab) {
  if (busy.value || (props.requiredSetup && value !== "general")) return;
  tab.value = value;
  pickerVisible.value = false;
  colorPickerVisible.value = false;
  showMessage("");
  if (value === "general") {
    loadSequence += 1;
    loading.value = false;
  } else if (value === "weight") {
    void loadWeightTab();
  } else void loadTab(value);
}

function formatCategoryWeight(weight: number) {
  return String(weight).replace(".", ",");
}

function onWeightCategorySelect(path: CategoryPathSelection) {
  weightCategoryId.value = Number(path.leafId) || "";
  weightCategoryLabel.value = path.pathLabel;
}

async function loadWeightTab() {
  const sequence = ++loadSequence;
  loading.value = true;
  try {
    const [rows, tree] = await Promise.all([
      getPrestashopCategoryWeights(),
      categoriesTree.value.length
        ? Promise.resolve(categoriesTree.value)
        : getCategoriesTree(),
    ]);
    if (sequence !== loadSequence) return;
    categoryWeights.value = rows;
    categoriesTree.value = tree;
  } catch (caught) {
    if (sequence === loadSequence)
      showMessage(
        caught instanceof Error ? caught.message : "Chargement impossible.",
        true
      );
  } finally {
    if (sequence === loadSequence) loading.value = false;
  }
}

async function saveCategoryWeightRule() {
  const categoryId = Number(weightCategoryId.value);
  const weightKg = Number(String(categoryWeightInput.value).replace(",", "."));
  if (!categoryId || !Number.isFinite(weightKg) || weightKg <= 0 || busy.value)
    return;
  busy.value = true;
  try {
    categoryWeights.value = await savePrestashopCategoryWeight(
      categoryId,
      weightKg
    );
    weightCategoryId.value = "";
    weightCategoryLabel.value = "";
    categoryWeightInput.value = "";
    weightFormKey.value += 1;
    emit("changed");
    showMessage(
      "Poids de catégorie enregistré. Le poids PrestaShop de ces produits est ignoré."
    );
  } catch (caught) {
    showMessage(
      caught instanceof Error ? caught.message : "Enregistrement impossible.",
      true
    );
  } finally {
    busy.value = false;
  }
}

async function removeCategoryWeightRule(categoryId: number) {
  if (busy.value) return;
  busy.value = true;
  try {
    categoryWeights.value = await deletePrestashopCategoryWeight(categoryId);
    emit("changed");
    showMessage("Règle de poids supprimée.");
  } catch (caught) {
    showMessage(
      caught instanceof Error ? caught.message : "Suppression impossible.",
      true
    );
  } finally {
    busy.value = false;
  }
}
async function saveGeneral() {
  if (
    !defaultGender.value ||
    !defaultProvenanceId.value ||
    !defaultSoldBy.value ||
    !defaultDeclinaisonId.value ||
    !defaultPackId.value ||
    busy.value
  )
    return;
  busy.value = true;
  try {
    const settings = await savePrestashopSettings(
      defaultGender.value,
      Number(defaultProvenanceId.value),
      priceAdjustmentPercent.value,
      priceIsUnit.value,
      weightIsUnit.value,
      defaultDeclinaisonId.value ? Number(defaultDeclinaisonId.value) : null,
      defaultPackId.value ? Number(defaultPackId.value) : null,
      defaultSoldBy.value
    );
    const settingChanged =
      savedGender.value !== settings.defaultGender ||
      savedProvenanceId.value !== settings.defaultProvenanceId;
    savedGender.value = settings.defaultGender;
    savedProvenanceId.value = settings.defaultProvenanceId;
    emit("settings-saved", settings);
    emit("changed");
    showMessage("Configuration enregistrée.");
    if (settingChanged) {
      pendingSummary.value = null;
      if (pendingLoading.value) {
        pendingSequence += 1;
        pendingLoading.value = false;
      }
      if (!props.requiredSetup) void loadPending(true);
    }
  } catch (caught) {
    showMessage(
      caught instanceof Error ? caught.message : "Enregistrement impossible.",
      true
    );
  } finally {
    busy.value = false;
  }
}
async function openPicker(source: string) {
  if (
    tab.value === "general" ||
    busy.value ||
    loading.value
  )
    return;
  busy.value = true;
  pickerSource.value = source;
  pickerSearch.value = "";
  brandName.value = source;
  brandListOpen.value = true;
  brandCreateOpen.value = false;
  try {
    if (tab.value === "color") {
      if (!officialColors.value.length)
        officialColors.value = await getCouleursOfficielles();
      colorPickerVisible.value = true;
    } else {
      if (tab.value === "category" && !categoriesTree.value.length)
        categoriesTree.value = await getCategoriesTree();
      pickerVisible.value = true;
    }
  } catch (caught) {
    showMessage(
      caught instanceof Error ? caught.message : "Sélecteur indisponible.",
      true
    );
  } finally {
    busy.value = false;
  }
}
async function savePickedTarget(targetId: number) {
  if (tab.value === "general" || busy.value || !pickerSource.value) return;
  busy.value = true;
  try {
    await savePrestashopMapping(tab.value, pickerSource.value, targetId);
    clearPending(tab.value, pickerSource.value);
    pickerVisible.value = false;
    colorPickerVisible.value = false;
    manualSource.value = "";
    await loadTab(tab.value);
    emit("changed");
    showMessage("Correspondance enregistrée.");
  } catch (caught) {
    showMessage(
      caught instanceof Error ? caught.message : "Enregistrement impossible.",
      true
    );
  } finally {
    busy.value = false;
  }
}
function onColorConfirm(colors: CouleurAPI[]) {
  const color = colors[0];
  colorPickerVisible.value = false;
  if (color) void savePickedTarget(color.id_couleur);
}
function onCategorySelect(path: CategoryPathSelection) {
  const targetId = Number(path.leafId);
  if (targetId) void savePickedTarget(targetId);
}
async function createBrand() {
  if (tab.value !== "brand" || busy.value || !brandName.value) return;
  busy.value = true;
  try {
    const result = await createPrestashopBrandMapping(
      pickerSource.value,
      brandName.value
    );
    clearPending("brand", pickerSource.value);
    pickerVisible.value = false;
    manualSource.value = "";
    await loadTab("brand");
    emit("changed");
    showMessage(
      result.createdBrand ? "Marque créée et liée." : "Marque existante liée."
    );
  } catch (caught) {
    showMessage(
      caught instanceof Error ? caught.message : "Création impossible.",
      true
    );
  } finally {
    busy.value = false;
  }
}
async function remove(row: PrestashopMapping) {
  if (tab.value === "general" || busy.value) return;
  busy.value = true;
  try {
    await deletePrestashopMapping(tab.value, row.sourceLabel);
    await loadTab(tab.value);
    if (pendingLoading.value) {
      pendingSequence += 1;
      pendingLoading.value = false;
    }
    void loadPending(true);
    emit("changed");
    showMessage("Correspondance supprimée.");
  } catch (caught) {
    showMessage(
      caught instanceof Error ? caught.message : "Suppression impossible.",
      true
    );
  } finally {
    busy.value = false;
  }
}

watch(
  () => props.visible,
  async (visible) => {
    if (!visible) return;
    tab.value = "general";
    loadSequence += 1;
    loading.value = false;
    showMessage("");
    try {
      const [settings, provenanceOptions, references] = await Promise.all([
        getPrestashopSettings(),
        getPrestashopProvenances(),
        getReferenceData(),
      ]);
      provenances.value = provenanceOptions;
      declinaisons.value = references.declinaisons || [];
      packs.value = references.packs || [];
      defaultGender.value = settings.defaultGender || "";
      savedGender.value = settings.defaultGender;
      priceAdjustmentPercent.value = [0, 5, 10].includes(
        Number(settings.priceAdjustmentPercent)
      )
        ? Number(settings.priceAdjustmentPercent)
        : 0;
      priceIsUnit.value = Boolean(settings.priceIsUnit);
      weightIsUnit.value = Boolean(settings.weightIsUnit);
      defaultProvenanceId.value = provenanceOptions.some(
        (option) => option.id === settings.defaultProvenanceId
      )
        ? Number(settings.defaultProvenanceId)
        : "";
      savedProvenanceId.value = settings.defaultProvenanceId;
      defaultSoldBy.value =
        settings.defaultSoldBy === "couleurs" ||
        settings.defaultSoldBy === "melangees" ||
        settings.defaultSoldBy === "tailles"
          ? settings.defaultSoldBy
          : "";
      defaultDeclinaisonId.value = declinaisons.value.some(
        (option) => Number(option.id) === Number(settings.defaultDeclinaisonId)
      )
        ? Number(settings.defaultDeclinaisonId)
        : "";
      defaultPackId.value = packs.value.some(
        (option) =>
          Number(option.id) === Number(settings.defaultPackId) &&
          !packOptionDisabled(option)
      )
        ? Number(settings.defaultPackId)
        : "";
      if (!props.requiredSetup) void loadPending();
    } catch (caught) {
      showMessage(
        caught instanceof Error
          ? caught.message
          : "Configuration indisponible.",
        true
      );
    }
  }
);
watch(
  () => props.shopUrl,
  () => {
    pendingSequence += 1;
    pendingSummary.value = null;
    pendingLoading.value = false;
    resolvedDuringScan.clear();
  }
);
watch(
  () => props.catalogRevision,
  () => {
    pendingSummary.value = null;
    if (props.visible && !props.requiredSetup) void loadPending(true);
  }
);
watch(defaultDeclinaisonId, () => {
  if (
    packsForDefaultDeclinaison.value.some(
      (pack) => Number(pack.id) === Number(defaultPackId.value)
    )
  )
    return;
  defaultPackId.value = "";
});
</script>
